<template>
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title" :data-success="isSuccess" ref="infoRequestModal" v-close-dialog="closeInfoRequest">
        <div class="wrapper">
            <button class="button fastic-close" id="InfoRequestClose" :aria-label="localizer.get('Contact.InfoRequest.Close')" @click="closeInfoRequest()"></button>
            <div class="scrollbar">
                <div v-if="!isSuccess" class="intro">
                    <div class="maintitle s40" id="modal-title">{{ localizer.get("App.Common.Name") }}</div>
                    <p class="maintitle s24 light">{{ localizer.get("Contact.InfoRequest.Abstract") }}</p>
                </div>
                <div v-if="!isSuccess" class="form" :aria-busy="isLoading">
                    <fieldset :disabled="isLoading">
                        <div class="item" data-label="top">
                            <label for="Type" class="maintitle s16">{{ localizer.get("App.Common.SelectArgument") }}</label>
                            <select id="Type" v-model="formData.type" :class="[{ 'input-validation-error': isValidationError('Type') }, 'input w460']">
                                <option :value="null">{{ localizer.get("App.Common.SelectArgument") }}</option>
                                <option v-for="(item, index) in types" :value="item.value" :key="index">{{ item.title }}</option>
                            </select>
                        </div>
                        <div class="item" data-label="top">
                            <label for="Message" class="maintitle s16">{{ localizer.get('App.Common.DescRequest') }}</label>
                            <textarea name="Message" id="Message" :placeholder="localizer.get('App.Common.DescRequest')" cols="30" rows="6" v-model.lazy="formData.message" :class="[{ 'input-validation-error': isValidationError('Message') }, 'input w650 textarea']"></textarea>
                        </div>
                    </fieldset>
                    <fieldset :disabled="isLoading">
                        <h2 class="maintitle s24">{{ localizer.get("App.Common.Help") }}</h2>
                        <div class="item">
                            <label for="Company" class="maintitle s16">{{ localizer.get("App.Common.Company") }}</label>
                            <input type="text" id="Company" v-model.lazy="formData.company" :class="[{ 'input-validation-error': isValidationError('Company') }, 'input w300']">
                        </div>
                        <div class="item">
                            <label for="Name" class="maintitle s16">{{ localizer.get("App.Common.NameSurname") }}</label>
                            <input type="text" id="Name" v-model.lazy="formData.name" :class="[{ 'input-validation-error': isValidationError('Name') }, 'input w300']">
                        </div>
                        <div class="item">
                            <label for="Email" class="maintitle s16">{{ localizer.get("App.Common.Email") }}</label>
                            <input type="email" id="Email" placeholder="name@domain.com" v-model.lazy="formData.email" :class="[{ 'input-validation-error': isValidationError('Email') }, 'input w300']">
                        </div>
                    </fieldset>
                    <fieldset :disabled="isLoading">
                        <div class="checkbox">
                            <input type="checkbox" id="DialogIsPrivacy" v-model.lazy="formData.isPrivacyPolicy">
                            <label for="DialogIsPrivacy" v-html="localizer.get('App.Form.Privacy', privacyPolicyUrl)"></label>
                        </div>
                        <div v-if="validationErrors.length !== 0" class="validation-summary-errors" role="alert" aria-live="polite">
                            <ul>
                                <li v-for="(validationError, index) in validationErrors" :key="index">{{ validationError.message }}</li>
                            </ul>
                        </div>
                        <div class="cta">
                            <button v-if="!isLoading" class="button" @click="formSubmit()">{{ localizer.get("App.Common.SubmitCta") }}</button>
                            <span v-else class="button">{{ localizer.get("App.Common.SubmitCta") }}</span>
                        </div>
                        <div class="item">
                            <div class="captcha-terms" v-html="localizer.get('App.Recaptcha.CaptchaTerms')"></div>
                        </div>
                    </fieldset>
                </div>
                <div v-else class="success">
                    <h2 class="maintitle s60">{{ localizer.get("App.Common.SubmitSuccess") }}</h2>
                    <p class="maindesc s16 light">{{ localizer.get("App.Common.SubmitSuccessMessage") }}</p>
                    <div class="cta">
                        <button class="button secondary" @click="closeInfoRequest()">{{ localizer.get("App.Common.GoNavigation") }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Axios from "axios";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";
import Localizer from "@Scripts/Common/Localizer";
import FormHelper from "@Scripts/Common/FormHelper";

const props = defineProps<{
    recaptchaSiteKey: string,
    requestUrl: string,
    privacyPolicyUrl: string
}>();

const store = useStore();
const route = useRoute();

const localizer = Localizer;
const routes = (window as any).App.routes;

const isLoading = ref(false);
const isSuccess = ref(false);
const types = ref<any[]>([]);
const formData = reactive<any>({ type: null });
const validationErrors = ref<any[]>([]);
const infoRequestModal = ref<HTMLElement | null>(null);

const isInfoRequestVisible = computed(() => store.getters["infoRequestStore/isInfoRequestVisible"]);

watch(isInfoRequestVisible, async (newValue) => {
    if (newValue) {
        await getData();
        document.body.classList.add("overflow");
        infoRequestModal.value?.classList.add("show");
    } else {
        document.body.classList.remove("overflow");
        infoRequestModal.value?.classList.remove("show");
    }
});

setOpenInfoRequest();

async function getData() {
    const result = (await Axios.post(routes.Support_GetInfoRequestData)).data;
    types.value = result.types;
}

async function formSubmit() {
    FormHelper.removeFocus();

    validationErrors.value = [];
    isLoading.value = true;

    (window as any).grecaptcha.enterprise.ready(async () => {
        const token = await (window as any).grecaptcha.enterprise.execute(props.recaptchaSiteKey, { action: 'Submit' });

        try {
            await Axios.post(routes.Support_InfoRequest, formData, { headers: { "RecaptchaVerificationToken": token } });
            isSuccess.value = true;
            GTagHelper.sendEvent("form_submit", "info_request", formData.type?.toLowerCase());
        } catch (error: any) {
            if (error.response && error.response.status === 400) {
                validationErrors.value = FormHelper.getValidationErrors(error.response.data);
            }
        }
        isLoading.value = false;
    });
}

function setOpenInfoRequest() {
    if (route.query.hasOwnProperty("inforequest")) {
        store.commit("infoRequestStore/setInfoRequestVisible", true);
    }
}

function closeInfoRequest() {
    store.commit("infoRequestStore/setInfoRequestVisible", false);
    isLoading.value = false;
    isSuccess.value = false;
    Object.assign(formData, { type: null });
    validationErrors.value = [];
}

function isValidationError(controlName: string): boolean {
    return FormHelper.isControlValidationError(controlName, validationErrors.value);
}
</script>